import React, { useState, useEffect, useContext, useCallback, memo } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import {
    PdfViewerComponent,
    Toolbar,
    Magnification,
    Navigation,
    Print,
    TextSelection,
    TextSearch,
    Inject,
} from '@syncfusion/ej2-react-pdfviewer';
import { PDFViewerContext } from 'contexts/PdfViewerContext';
import { accessToken$ } from 'services/subjects';
import styles from './PDFViewer.module.scss';
import Button from '@mui/material/Button';
import { AnimatePresence, motion } from "motion/react"
import documentService from 'services/documentService';

export const PDFViewer = memo(function PDFViewer() {
    const { dialogOpen, documents, setDialogOpen, removeDocument, lastOpenDocument } = useContext(PDFViewerContext);
    const [value, setValue] = useState(0);
    const [accessToken, setAccessToken] = useState(0);

    const handleChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    }, []);

    const handleClickMyDocuments = useCallback(() => {
        setDialogOpen(true);
    }, []);

    const handleClickCloseDocument = useCallback((documentPath: string) => {
        removeDocument(documentPath);
    }, []);

    useEffect(() => {
        accessToken$.subscribe({
            next: (token) => setAccessToken(token),
        });
    }, []);

    useEffect(() => {
        if (!lastOpenDocument) {
            return;
        }
        setValue(documents.findIndex((document) => document.documentPath === lastOpenDocument) || 0);
    }, [lastOpenDocument, documents]);

    function handleClickCloseViewer() {
        setDialogOpen(false);
    }

    function handlePrintStart(documentId: string) {
        documentService.trackPrintStarted$(documentId).subscribe();
    }
    function handleDownloadStart(documentId: string) {
        documentService.trackDownloadStarted$(documentId).subscribe();
    }

    return (
        <div className={styles.PDFViewer}>
            <div className={styles['floating-btn-container']}>
                {!!documents.length && (
                    <Button
                        variant="contained"
                        className={styles['floating-btn']}
                        onClick={handleClickMyDocuments}
                    >
                        <span>My Documents</span>
                        <span className={styles['icon-container']}>
                            <DescriptionIcon fontSize="large" />
                            <span className={styles['badge']}>{documents.length}</span>
                        </span>
                    </Button>
                )}
            </div>
            <>
            <AnimatePresence>
                {dialogOpen && <motion.article initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                     <article onClick={handleClickCloseViewer}  className={styles['dialog-background']}/>
                </motion.article>}
            </AnimatePresence>
            <article  className={`${styles['dialog-container']} ${dialogOpen && styles.open}}`}>
                <article className={`${styles.dialog} ${dialogOpen && styles.open}`}>
                    <article>
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {documents.map((document, index) => (
                                <Tab
                                    className={`${styles.tab} ${value === index && styles.active}`}
                                    key={document.documentPath}
                                    label={
                                        <>
                                            <span className={styles.text}>{document.displayName}</span>
                                            <CloseIcon
                                                className={styles.close}
                                                onClick={handleClickCloseDocument.bind(
                                                    {},
                                                    document.documentPath
                                                )}
                                            />
                                        </>
                                    }
                                />
                            ))}
                        </Tabs>
                    </article>
                    <article className={styles['dialog-body']}>
                    {documents.map(
                        (document, index) =>
                            (
                                <article key={document.documentPath} className={`${styles.document} ${value === index && styles.open}`}>
                                    <PdfViewerComponent
                                        documentPath={document.documentPath}
                                        serviceUrl={document.serviceUrl}
                                        className={styles.viewer}
                                        printStart={handlePrintStart.bind({}, document.documentPath)}
                                        downloadStart={handleDownloadStart.bind({}, document.documentPath)}
                                        ajaxRequestSettings={{
                                            ajaxHeaders: [
                                                {
                                                    headerName: 'Authorization',
                                                    headerValue: `Bearer ${accessToken}`,
                                                },
                                            ],
                                        }}
                                    >
                                        <Inject
                                            services={[
                                                Toolbar,
                                                Magnification,
                                                Navigation,
                                                Print,
                                                TextSelection,
                                                TextSearch,
                                            ]}
                                        />
                                    </PdfViewerComponent>
                                </article>
                            )
                    )}
                    </article>
            </article>
            </article>
            </>
        </div>
    );
});
